import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type PolicyType = 'privacy' | 'terms' | 'refund';
interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: PolicyType;
}
const policyContent = {
  privacy: {
    title: 'Privacy Policy',
    content: <>
        <p className="mb-4" data-id="element-866">
          <strong data-id="element-867">Last Updated:</strong> December 22, 2025
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-868">
          Information We Collect
        </h3>
        <p className="mb-4" data-id="element-869">
          We collect information you provide directly to us when you make a
          purchase, create an account, or communicate with us. This includes
          your name, email address, shipping address, payment information, and
          phone number.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-870">
          How We Use Your Information
        </h3>
        <p className="mb-4" data-id="element-871">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-872">
          <li data-id="element-873">Process and fulfill your orders</li>
          <li data-id="element-874">Send you order confirmations and shipping updates</li>
          <li data-id="element-875">Respond to your customer service requests</li>
          <li data-id="element-876">Send you marketing communications (with your consent)</li>
          <li data-id="element-877">Improve our products and services</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-878">
          Information Sharing
        </h3>
        <p className="mb-4" data-id="element-879">
          We do not sell or rent your personal information to third parties. We
          may share your information with:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-880">
          <li data-id="element-881">Service providers who help us operate our business</li>
          <li data-id="element-882">Payment processors to complete transactions</li>
          <li data-id="element-883">Shipping carriers to deliver your orders</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-884">
          Data Security
        </h3>
        <p className="mb-4" data-id="element-885">
          We implement appropriate technical and organizational measures to
          protect your personal information against unauthorized access,
          alteration, disclosure, or destruction.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-886">
          Your Rights
        </h3>
        <p className="mb-4" data-id="element-887">
          You have the right to access, correct, or delete your personal
          information. You may also opt out of marketing communications at any
          time.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-888">
          Contact Us
        </h3>
        <p className="mb-4" data-id="element-889">
          If you have questions about this Privacy Policy, please contact us at
          privacy@ginext.com
        </p>
      </>
  },
  terms: {
    title: 'Terms of Service',
    content: <>
        <p className="mb-4" data-id="element-890">
          <strong data-id="element-891">Last Updated:</strong> December 22, 2025
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-892">
          Agreement to Terms
        </h3>
        <p className="mb-4" data-id="element-893">
          By accessing or using GINEXT products and services, you agree to be
          bound by these Terms of Service. If you do not agree to these terms,
          please do not use our services.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-894">
          Product Information
        </h3>
        <p className="mb-4" data-id="element-895">
          We strive to provide accurate product descriptions and pricing.
          However, we do not warrant that product descriptions, pricing, or
          other content is accurate, complete, reliable, current, or error-free.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-896">
          Orders and Payment
        </h3>
        <p className="mb-4" data-id="element-897">
          By placing an order, you represent that you are legally capable of
          entering into binding contracts. All payments must be made in full at
          the time of purchase.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-898">
          Shipping and Delivery
        </h3>
        <p className="mb-4" data-id="element-899">
          We ship to addresses within the United States. Shipping times may vary
          based on your location. Free shipping is available on orders over $50.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-900">
          Product Use
        </h3>
        <p className="mb-4" data-id="element-901">
          GINEXT products are for external use only. Discontinue use if
          irritation occurs. These statements have not been evaluated by the
          FDA. This product is not intended to diagnose, treat, cure, or prevent
          any disease.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-902">
          Limitation of Liability
        </h3>
        <p className="mb-4" data-id="element-903">
          GINEXT shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages resulting from your use of our
          products or services.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-904">
          Changes to Terms
        </h3>
        <p className="mb-4" data-id="element-905">
          We reserve the right to modify these terms at any time. Continued use
          of our services after changes constitutes acceptance of the modified
          terms.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-906">
          Contact Information
        </h3>
        <p className="mb-4" data-id="element-907">
          For questions about these Terms of Service, contact us at
          support@ginext.com
        </p>
      </>
  },
  refund: {
    title: '30-Day Refund Policy',
    content: <>
        <p className="mb-4" data-id="element-908">
          <strong data-id="element-909">Last Updated:</strong> December 22, 2025
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-910">
          Our Guarantee
        </h3>
        <p className="mb-4" data-id="element-911">
          We stand behind the quality of GINEXT. If you're not completely
          satisfied with your purchase, we offer a 30-day money-back guarantee
          on your initial order.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-912">
          Eligibility
        </h3>
        <p className="mb-4" data-id="element-913">To be eligible for a refund, you must:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-914">
          <li data-id="element-915">
            Request a refund within 30 days of your original purchase date
          </li>
          <li data-id="element-916">
            Contact our customer service team to initiate the return process
          </li>
          <li data-id="element-917">Provide your order number and reason for return</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-918">
          Refund Process
        </h3>
        <p className="mb-4" data-id="element-919">Once we receive your refund request:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-920">
          <li data-id="element-921">
            We'll provide you with a return authorization and instructions
          </li>
          <li data-id="element-922">You'll need to return the product (opened or unopened)</li>
          <li data-id="element-923">
            Once we receive the return, we'll process your refund within 5-7
            business days
          </li>
          <li data-id="element-924">Refunds will be issued to your original payment method</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-925">
          Shipping Costs
        </h3>
        <p className="mb-4" data-id="element-926">
          Original shipping costs are non-refundable. Return shipping costs are
          the responsibility of the customer unless the return is due to our
          error or a defective product.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-927">
          Exclusions
        </h3>
        <p className="mb-4" data-id="element-928">The following are not eligible for refunds:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-929">
          <li data-id="element-930">Products purchased more than 30 days ago</li>
          <li data-id="element-931">Products purchased from unauthorized retailers</li>
          <li data-id="element-932">
            Products that have been tampered with or damaged due to misuse
          </li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-933">Exchanges</h3>
        <p className="mb-4" data-id="element-934">
          If you received a damaged or defective product, we'll replace it at no
          additional cost. Contact us immediately with photos of the damage.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-935">
          Contact Us
        </h3>
        <p className="mb-4" data-id="element-936">
          To initiate a refund or if you have questions about our refund policy:
        </p>
        <ul className="list-none pl-0 mb-4 space-y-2" data-id="element-937">
          <li data-id="element-938">
            <strong data-id="element-939">Email:</strong> support@ginextwarming.com
          </li>

          <li data-id="element-940">
            <strong data-id="element-941">Hours:</strong> Monday-Friday, 9am-5pm EST
          </li>
        </ul>

        <p className="mb-4 text-sm text-charcoal-light italic" data-id="element-942">
          We're committed to your satisfaction. If you're not happy with GINEXT,
          we want to make it right.
        </p>
      </>
  }
};
export function PolicyModal({
  isOpen,
  onClose,
  type
}: PolicyModalProps) {
  const policy = policyContent[type];
  return <AnimatePresence data-id="element-943">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" data-id="element-944" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-945">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden pointer-events-auto" data-id="element-946">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-bone-dark px-6 py-4 flex items-center justify-between z-10" data-id="element-947">
                <h2 className="text-2xl font-bold text-charcoal" data-id="element-948">
                  {policy.title}
                </h2>
                <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-bone transition-colors flex items-center justify-center" data-id="element-949">
                  <X className="w-5 h-5 text-charcoal" data-id="element-950" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-951">
                <div className="prose prose-sm max-w-none text-charcoal-light leading-relaxed" data-id="element-952">
                  {policy.content}
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-bone border-t border-bone-dark px-6 py-4" data-id="element-953">
                <button onClick={onClose} className="w-full bg-ginger text-white py-3 rounded-lg font-semibold hover:bg-ginger/90 transition-colors" data-id="element-954">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}